<script setup lang="ts" generic="T">
import type { Table } from '@tanstack/vue-table';

import { GripVertical, RotateCcw, Settings2 } from 'lucide-vue-next';
import Sortable from 'sortablejs';

import { CALL_FIELD_LABELS } from '@/pages/calls/data/fields';
import { Button } from '@/components/ui/button';

interface DataTableViewOptionsProps {
  table: Table<T>;
  onResetSettings?: () => void;
}

const props = defineProps<DataTableViewOptionsProps>();

// Track column visibility state separately for reactivity
const columnVisibility = ref<Record<string, boolean>>({});

// Get all hideable columns
const columns = computed(() =>
  props.table
    .getAllColumns()
    .filter(column => typeof column.accessorFn !== 'undefined' && column.getCanHide()),
);

// Initialize column visibility state
watchEffect(() => {
  const visibility: Record<string, boolean> = {};
  columns.value.forEach(col => {
    const isVisible = col.getIsVisible();
    visibility[col.id] = isVisible;
  });
  columnVisibility.value = visibility;
});

const columnListEl = ref<HTMLElement | null>(null);
const sortableInstance = ref<Sortable | null>(null);
const isOpen = ref(false);

// Initialize Sortable when dropdown opens
function handleOpenChange(open: boolean) {
  isOpen.value = open;

  if (open) {
    // Wait for DOM to update before initializing Sortable
    nextTick(() => {
      if (columnListEl.value) {
        // Destroy existing instance if it exists
        if (sortableInstance.value) {
          sortableInstance.value.destroy();
          sortableInstance.value = null;
        }

        sortableInstance.value = Sortable.create(columnListEl.value, {
          animation: 150,
          handle: '.drag-handle',
          filter: 'button, input, label, [role="checkbox"]', // Exclude interactive elements from dragging
          preventOnFilter: false, // Allow normal interaction with filtered elements
          ghostClass: 'opacity-50',
          dragClass: 'shadow-lg',
          onStart: () => {
            // Handle drag start
          },
          onEnd: () => {
            // Get the column order after drag
            const items = columnListEl.value?.querySelectorAll('[data-column-id]');
            if (!items) {return;}

            const newOrder: string[] = [];
            items.forEach(item => {
              const columnId = item.getAttribute('data-column-id');
              if (columnId) {newOrder.push(columnId);}
            });

            // Update column order in the table
            props.table.setColumnOrder(newOrder);
          },
        });
      }
    });
  } else {
    // Destroy Sortable when dropdown closes
    if (sortableInstance.value) {
      sortableInstance.value.destroy();
      sortableInstance.value = null;
    }
  }
}

// Cleanup on unmount
onUnmounted(() => {
  if (sortableInstance.value) {
    sortableInstance.value.destroy();
  }
});

// Get label from CALL_FIELD_LABELS or fallback to formatted column ID
function getColumnLabel(columnId: string): string {
  if (columnId in CALL_FIELD_LABELS) {
    return CALL_FIELD_LABELS[columnId as keyof typeof CALL_FIELD_LABELS];
  }
  // Fallback: format snake_case to Title Case
  return columnId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Handle checkbox toggle
function toggleColumnVisibility(columnId: string, value: boolean | 'indeterminate') {
  // Convert 'indeterminate' to boolean (treat as false)
  const boolValue = value === true;

  const column = columns.value.find(c => c.id === columnId);
  if (column) {
    column.toggleVisibility(boolValue);
    columnVisibility.value[columnId] = boolValue;
  }
}
</script>

<template>
  <UiDropdownMenu @update:open="handleOpenChange">
    <UiDropdownMenuTrigger as-child>
      <UiButton variant="outline" size="sm" class="hidden h-8 ml-auto lg:flex">
        <Settings2 class="size-4 mr-2" />
        View
      </UiButton>
    </UiDropdownMenuTrigger>
    <UiDropdownMenuContent align="end" class="w-[260px]">
      <UiDropdownMenuLabel class="flex items-center justify-between">
        <span>Manage Columns</span>
        <span class="text-xs font-normal text-muted-foreground">
          {{ Object.values(columnVisibility).filter(v => v).length }} / {{ columns.length }}
        </span>
      </UiDropdownMenuLabel>
      <UiDropdownMenuSeparator />

      <div class="px-2 py-2">
        <Button
          v-if="props.onResetSettings"
          variant="outline"
          size="sm"
          class="w-full"
          @click="props.onResetSettings"
        >
          <RotateCcw class="size-4 mr-2" />
          Reset to Default
        </Button>
      </div>
      <UiDropdownMenuSeparator v-if="props.onResetSettings" />

      <div ref="columnListEl" class="max-h-[400px] overflow-y-auto">
        <div
          v-for="column in columns"
          :key="column.id"
          :data-column-id="column.id"
          class="group flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded-sm transition-colors"
        >
          <GripVertical
            class="drag-handle size-4 shrink-0 text-muted-foreground cursor-grab active:cursor-grabbing"
          />
          <UiCheckbox
            :id="`column-${column.id}`"
            :model-value="columnVisibility[column.id]"
            @update:model-value="
              (value: boolean | 'indeterminate') => toggleColumnVisibility(column.id, value)
            "
          />
          <label
            :for="`column-${column.id}`"
            class="flex-1 text-sm truncate cursor-pointer select-none"
          >
            {{ getColumnLabel(column.id) }}
          </label>
        </div>
      </div>
    </UiDropdownMenuContent>
  </UiDropdownMenu>
</template>
